import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Table, { ITableColumn } from '@/component/table/Table.tsx'
import Pagination from '@/component/pagination/Pagination.tsx'
import useQueryGetNotificationFallDetection, {
    IAlertFallDetectionItem,
    FallDetectionResultType
} from '@/api/notification/getNotificationFallDetection.ts'
import {
    EmergencyAlertType,
    EmergencyAlertConfirmStatus
} from '@/api/notification/getNotificationSos.ts'
import { format } from 'date-fns'
import AlertConfirmStatusBadge from '@/component/badge/AlertConfirmStatusBadge.tsx'
import SearchBar from '@/component/input/SearchBar.tsx'
import useDebounce from '@/util/hook/useDebounce.tsx'
import ExcelDownloadIcon from '@/assets/ic_notification_sos_excel_download.svg?react'
import Tooltip from '@/component/tooltip/Tooltip.tsx'
import Input from '@/component/input/Input.tsx'
import SolidButton from '@/component/button/SolidButton.tsx'
import { isEmpty } from '@/util/strings.ts'
import { createUrl } from '@/util/url.ts'
import { apiDownload } from '@/api/api.ts'
import { useLoginStore } from '@/store/LoginStore.ts'
import EmergencyAlertModal from '@/pages/notification/emergency/EmergencyAlertModal.tsx'
import { useTranslation } from 'react-i18next'
import useQueryGetMe from '@/api/auth/getMe.ts'

export interface IProps {
    className?: string
}

const PAGE_SIZE = 10

const NotificationFalldetectionContainer: React.FC<IProps> = ({ className }) => {
    const { t } = useTranslation()
    const { data: me } = useQueryGetMe()
    const accessToken = useLoginStore(state => state.accessToken)
    const isLoggedIn = !!accessToken

    const [currentPage, setCurrentPage] = useState(1)
    const [search, setSearch] = useState('')
    const [year, setYear] = useState('')
    const [month, setMonth] = useState('')
    const [detailModalId, setDetailModalId] = useState<number | undefined>(undefined)
    const detailModalRef = useRef<any>()
    const debouncedSearchText = useDebounce(search, 200)

    // @ts-ignore
    const isMaskingRequired = useMemo(() => {
        // @ts-ignore
        return me?.email === 'dbd_cm@clomental.com'
    }, [me])

    const getMaskedName = (name?: string) => {
        if (!isMaskingRequired || !name) return name
        if (name.length <= 1) return name
        return name.charAt(0) + '**'
    }

    const getMaskedContact = (contact?: string) => {
        if (!isMaskingRequired || !contact) return contact
        const contactParts = contact?.split('-')
        if (contactParts?.length === 3) {
            return `${contactParts[0]}-****-****`
        }
        if (contact?.length > 3) {
            return contact.substring(0, 3) + contact.substring(3).replace(/./g, '*')
        }
        return contact
    }

    const getMaskedAddress = (address?: string) => {
        if (!isMaskingRequired || !address) return address
        return '****'
    }

    useEffect(() => {
        setCurrentPage(1)
    }, [debouncedSearchText])

    const { data, refetch } = useQueryGetNotificationFallDetection({
        pageNum: currentPage,
        pageSize: PAGE_SIZE
    }, debouncedSearchText);

    const getTitle = useCallback(() => {
        return t('notification.falldetection.title');
    }, [t]);

    const getExcelParams = useCallback(() => {
        const params: {
            year: string;
            month: string;
        } = {
            year: year,
            month: month,
        };
        return params;
    }, [year, month]);

    const getExcelFileName = useCallback(() => {
        return `케얼리_낙상감지_${year}_${month}.xlsx`;
    }, [year, month]);

    const getGenderName = useCallback((value: string) => {
        switch (value) {
            case '남':
                return t('notification.falldetection.table.gender.male')
            case '여':
                return t('notification.falldetection.table.gender.female')
            default:
                return value
        }
    }, [t])

    const getResultName = useCallback((value: string | null) => {
        if (!value) return '-'
        switch (value) {
            case FallDetectionResultType.EMERGENCY_119:
                return t('notification.falldetection.table.process.119')
            case FallDetectionResultType.MALFUNCTION:
                return t('notification.falldetection.table.process.malfunction')
            case FallDetectionResultType.ETC:
                return t('notification.falldetection.table.process.etc')
            default:
                return value
        }
    }, [t])

    const columns = useMemo<ITableColumn<IAlertFallDetectionItem>[]>(() => {
        const result: ITableColumn<IAlertFallDetectionItem>[] = [
            {
                key: 'alert_time',
                header: () => <p
                    className={'font-medium text-[14px]'}>{t('notification.falldetection.table.alertTime.column')}</p>,
                thClassName: 'bg-[#F0F5FF]',
                cell: (item: IAlertFallDetectionItem) => {
                    return <div
                        className={'text-center cursor-pointer hover:text-blue-500'}
                        onClick={() => {
                            if (item?.id) {
                                setDetailModalId(item.id);
                                detailModalRef.current?.show();
                            }
                        }}
                    >
                        {item?.alert?.createdAt && format(new Date(item.alert.createdAt), 'yyyy/MM/dd HH:mm')}
                    </div>
                }
            },
            {
                key: 'checked_time',
                header: () => <p
                    className={'font-medium text-[14px]'}>{t('notification.falldetection.table.confirmedTime.column')}</p>,
                thClassName: 'bg-[#F0F5FF]',
                cell: (item: IAlertFallDetectionItem) => {
                    return <p
                        className={'text-center'}>{item?.alert?.checkedAt && format(new Date(item.alert.checkedAt), 'yyyy/MM/dd HH:mm')}</p>
                }
            },
            {
                key: 'manager_name',
                header: () => <p className={'font-medium text-[14px]'}>{t('notification.falldetection.table.manager.column')}</p>,
                thClassName: 'bg-[#F0F5FF]',
                cell: (item: IAlertFallDetectionItem) => {
                    return <p
                        className={'text-center'}>{getMaskedName(item?.alert?.managerName || undefined)}</p>
                }
            },
            {
                key: 'protectee_name',
                header: () => <p
                    className={'font-medium text-[14px]'}>{t('notification.falldetection.table.userName.column')}</p>,
                thClassName: 'bg-[#F0F5FF]',
                cell: (item: IAlertFallDetectionItem) => {
                    return <p
                        className={'text-center'}>{getMaskedName(item?.protectee?.name)}</p>
                }
            },
            {
                key: 'protectee_gender_birthday',
                header: () => <p
                    className={'font-medium text-[14px]'}>{t('notification.falldetection.table.gender.column')} / {t('notification.falldetection.table.birthday.column')}</p>,
                thClassName: 'bg-[#F0F5FF]',
                cell: (item: IAlertFallDetectionItem) => {
                    return <p
                        className={'text-center'}>{getGenderName(item?.protectee?.gender)} / {isMaskingRequired ? '**-**-**' : (item?.protectee?.birthDate && format(new Date(item.protectee.birthDate), 'yy-MM-dd'))}</p>
                }
            },
            {
                key: 'protectee_contact',
                header: () => <p className={'font-medium text-[14px]'}>{t('notification.falldetection.table.contact.column')}</p>,
                thClassName: 'bg-[#F0F5FF]',
                cell: (item: IAlertFallDetectionItem) => {
                    return <p
                        className={'text-center'}>{getMaskedContact(item?.protectee?.contact)}</p>
                }
            },
            {
                key: 'alert_location',
                header: () => <p className={'font-medium text-[14px]'}>{t('notification.falldetection.table.address.column')}</p>,
                thClassName: 'bg-[#F0F5FF]',
                cell: (item: IAlertFallDetectionItem) => {
                    return <p
                        className={'text-center'}>{getMaskedAddress(item?.alert?.location?.address)}</p>
                }
            },
            {
                key: 'status',
                header: () => <p className={'font-medium text-[14px]'}>{t('notification.falldetection.table.status.column')}</p>,
                thClassName: 'bg-[#F0F5FF]',
                cell: (item: IAlertFallDetectionItem) => {
                    let confirmStatus: EmergencyAlertConfirmStatus;
                    if (item?.alert?.confirm === '처리') {
                        confirmStatus = EmergencyAlertConfirmStatus.DONE;
                    } else if (item?.alert?.confirm === '담당자 배정') {
                        confirmStatus = EmergencyAlertConfirmStatus.ASSIGN;
                    } else {
                        confirmStatus = EmergencyAlertConfirmStatus.WAIT;
                    }
                    return <div className={'flex justify-center items-center'}><AlertConfirmStatusBadge
                        value={confirmStatus} /></div>
                }
            },
            {
                key: 'alert_type',
                header: () => <p className={'font-medium text-[14px]'}>{t('notification.falldetection.table.process.column')}</p>,
                thClassName: 'bg-[#F0F5FF]',
                cell: (item: IAlertFallDetectionItem) => {
                    return <p
                        className={'text-center'}>{getResultName(item?.alert?.result)}</p>
                }
            }
        ]
        return result
    }, [isMaskingRequired, t, getGenderName, getResultName, getMaskedName, getMaskedContact, getMaskedAddress])

    return <>
        <div className={`${className}`}>
            <div className={'flex py-[15px] px-[20px] items-center justify-between gap-[20px]'}>
                <h2 className="text-xl font-bold">{getTitle()}</h2>
                <div className="flex items-center gap-[20px]">
                    <Tooltip
                        render={<div className={'flex items-center font-medium text-main_b gap-[6px]'} title={t('notification.falldetection.excel')}>
                            <span>{t('notification.falldetection.excel')}</span>
                            <ExcelDownloadIcon />
                        </div>}>
                        <p className={'font-bold'}>{t('notification.falldetection.excelModal.title')}</p>
                        <div className={'flex items-center gap-[5px] py-[10px]'}>
                            <Input inputType={'number'} className={'max-w-[60px] '}
                                value={year}
                                onChanged={text => setYear(text)} />
                            {t('notification.falldetection.excelModal.year')} <Input
                                className={'max-w-[60px]'}
                                value={month}
                                onChanged={text => setMonth(text)} />{t('notification.falldetection.excelModal.month')}
                            <SolidButton text={t('notification.falldetection.excelModal.download')} className={'ml-[20px]'}
                                disabled={isEmpty(year) || isEmpty(month)}
                                onClick={async () => {
                                    if (!isLoggedIn) return
                                    const url = createUrl(`/alert/falldetection/excel`, getExcelParams());
                                    const urlBlob = await apiDownload(url);

                                    const downloadUrl = window.URL.createObjectURL(urlBlob as any);
                                    const link = document.createElement('a');
                                    link.href = downloadUrl;
                                    link.setAttribute('download', getExcelFileName());
                                    document.body.appendChild(link);
                                    link.click();
                                    link.remove();
                                    window.URL.revokeObjectURL(downloadUrl);
                                }} />
                        </div>
                    </Tooltip>
                    <SearchBar className="w-[250px]" placeholder={t('notification.falldetection.searchPlaceholder')} value={search}
                        onChanged={text => setSearch(text)} />
                </div>
            </div>
            <Table
                data={data?.data || []}
                columns={columns}
                extractKey={(item: IAlertFallDetectionItem) => `${item?.id}`}
                renderEmpty={<div className={'flex items-center justify-center h-full min-h-[200px]'}><p
                    className={'text-center'}>{t('notification.falldetection.table.empty')}</p></div>}
                className={'text-[14px]'}
                itemRowClassName={() => 'border-b border-solid border-gray2'}
                itemCommonClassName={() => 'py-[10px] px-[22px]'}
                onClickRow={(item: IAlertFallDetectionItem) => {
                    if (item?.id) {
                        setDetailModalId(item.id)
                        detailModalRef.current?.show()
                    }
                }}
            />
            {(data?.pagination?.totalPages || 0) > 0 && <Pagination
                className={'mt-[25px]'}
                current_page={data?.pagination?.currentPage}
                page_size={PAGE_SIZE}
                total_page={data?.pagination?.totalPages}
                fetchPage={(pageIndex) => {
                    setCurrentPage(pageIndex)
                }} />}
        </div>
        {detailModalId !== undefined &&
            <EmergencyAlertModal
                ref={detailModalRef}
                defaultId={detailModalId}
                refetchAlerts={refetch}
                defaultTab={EmergencyAlertType.FALL}
                isLiveMode={false}
            />}
    </>
}

export default NotificationFalldetectionContainer 
